import { applyPageFocusStates } from '@/common/utils/focusLayoutPageState'
import { mergeExchangeStatesByKey } from '@/common/utils/normalizeExchangeStates'
import { sameWindowSchemaUnchangedOnPage } from '@/common/utils/sameWindowFormRefetch'

export const PRESERVED_PAGE_TABS_KEY = '$preservedPageTabs'

export type PreservedPageTab = {
  id: string
  name: string
  enabled?: boolean
  focused?: boolean
}

function isPageState(state: unknown): state is Record<string, unknown> {
  if (!state || typeof state !== 'object') return false
  return String((state as Record<string, unknown>).element_level ?? '').trim().toUpperCase() === 'PAGE'
}

function readBoolean(value: unknown): boolean | undefined {
  return value === true || value === false ? value : undefined
}

export function readPreservedPageTabs(page: unknown): PreservedPageTab[] {
  const states = (page as { jsResult?: { states?: unknown } } | null | undefined)?.jsResult?.states
  if (!Array.isArray(states)) return []
  const rows: PreservedPageTab[] = []
  for (const state of states) {
    if (!isPageState(state)) continue
    const id = String(state.element_id ?? '').trim()
    const name = String(state.element_name ?? '').trim()
    if (!id && !name) continue
    const enabled = readBoolean(state.is_enabled)
    const focused = state.is_focused === true
    if (enabled === undefined && !focused) continue
    rows.push({
      id,
      name,
      ...(enabled !== undefined ? { enabled } : {}),
      ...(focused ? { focused: true } : {}),
    })
  }
  return rows
}

export function applyPreservedPageTabRows(states: unknown[] | null | undefined, rows: PreservedPageTab[]): any[] {
  let next = Array.isArray(states) ? [...states] : []
  const enabledPatches = rows
    .filter((row) => row.enabled !== undefined && (row.id || row.name))
    .map((row) => ({
      element_level: 'PAGE',
      ...(row.id ? { element_id: /^\d+$/.test(row.id) ? Number(row.id) : row.id } : {}),
      ...(row.name && row.name !== row.id ? { element_name: row.name } : {}),
      is_enabled: row.enabled,
    }))
  if (enabledPatches.length > 0) {
    next = mergeExchangeStatesByKey(next, enabledPatches)
  }
  const focused = [...rows].reverse().find((row) => row.focused && (row.id || row.name))
  if (focused) {
    next = applyPageFocusStates(next, { id: focused.id, jsName: focused.name })
  }
  return next
}

/** Same-schema SAME_WINDOW replace drops the tab the client script just focused. */
export function overlayPreservedPageTabs<T extends Record<string, unknown>>(
  previous: unknown,
  next: T,
): T {
  const rows = readPreservedPageTabs(previous)
  if (rows.length === 0) return next
  const jsResult =
    next.jsResult && typeof next.jsResult === 'object'
      ? (next.jsResult as Record<string, unknown>)
      : {}
  const states = applyPreservedPageTabRows(
    Array.isArray(jsResult.states) ? (jsResult.states as unknown[]) : [],
    rows,
  )
  return {
    ...next,
    jsResult: {
      ...jsResult,
      states,
    },
    [PRESERVED_PAGE_TABS_KEY]: rows,
  }
}

export function preserveSameSchemaPageTabs<T extends Record<string, unknown>>(
  previous: Record<string, unknown> | null | undefined,
  next: T,
  slotId: string,
): T {
  if (!previous || !sameWindowSchemaUnchangedOnPage(previous, next, slotId)) return next
  return overlayPreservedPageTabs(previous, next)
}

export function reapplyPreservedPageTabs(page: unknown): boolean {
  if (!page || typeof page !== 'object') return false
  const host = page as Record<string, unknown>
  const rows = host[PRESERVED_PAGE_TABS_KEY]
  if (!Array.isArray(rows) || rows.length === 0) return false
  const jsResult =
    host.jsResult && typeof host.jsResult === 'object'
      ? (host.jsResult as Record<string, unknown>)
      : {}
  const states = applyPreservedPageTabRows(
    Array.isArray(jsResult.states) ? (jsResult.states as unknown[]) : [],
    rows as PreservedPageTab[],
  )
  host.jsResult = { ...jsResult, states }
  delete host[PRESERVED_PAGE_TABS_KEY]
  return true
}

export function clearPreservedPageTabMarker(page: unknown): void {
  if (!page || typeof page !== 'object') return
  const host = page as Record<string, unknown>
  if (PRESERVED_PAGE_TABS_KEY in host) delete host[PRESERVED_PAGE_TABS_KEY]
}
